import React, { useEffect, useReducer, useState, useContext } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Tooltip,
  Fade,
  useTheme,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

// Icons
import { DeleteOutline, Edit, Add as AddIcon } from "@material-ui/icons";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ChatIcon from "@material-ui/icons/Chat";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    minHeight: "calc(100vh - 70px)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  // Header
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.4)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  // Add Button
  addButton: {
    background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
    color: "#fff",
    borderRadius: 10,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #D97706 0%, #DC2626 100%)",
      boxShadow: "0 6px 20px rgba(245, 158, 11, 0.5)",
    },
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 150,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // Table
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  tableWrapper: {
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableHead-root": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      "& .MuiTableCell-head": {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        padding: theme.spacing(2),
        whiteSpace: "nowrap",
      },
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      "& .MuiTableCell-body": {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      },
    },
  },
  // Queue Row
  queueInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  queueColor: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.85rem",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  queueName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.9rem",
  },
  colorChip: {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.75rem",
    minWidth: 80,
    justifyContent: "center",
  },
  greetingText: {
    maxWidth: 250,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  orderBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  // Action Buttons
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: alpha("#F59E0B", 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    color: "#F59E0B",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];
    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });
    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);
    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-queue`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("queues.toasts.success"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <Box className={classes.root}>
      {/* Modals */}
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />

      {/* Header */}
      <Fade in timeout={400}>
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Box className={classes.titleSection}>
              <Box className={classes.titleIcon}>
                <AccountTreeIcon />
              </Box>
              <Box>
                <Typography className={classes.title}>
                  {i18n.t("queues.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  Configure filas de atendimento e chatbots
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleOpenQueueModal}
            >
              {i18n.t("queues.buttons.add")}
            </Button>
          </Box>

          {/* Stats */}
          <Box className={classes.statsRow}>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#F59E0B", 0.15) }}
              >
                <AccountTreeIcon style={{ color: "#F59E0B" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{queues.length}</Typography>
                <Typography className={classes.statLabel}>Total de Filas</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha(theme.palette.primary.main, 0.15) }}
              >
                <ChatIcon style={{ color: theme.palette.primary.main }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>
                  {queues.filter((q) => q.greetingMessage).length}
                </Typography>
                <Typography className={classes.statLabel}>Com Chatbot</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={600}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.tableWrapper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("queues.table.id")}</TableCell>
                  <TableCell>{i18n.t("queues.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("queues.table.color")}</TableCell>
                  <TableCell align="center">{i18n.t("queues.table.orderQueue")}</TableCell>
                  <TableCell>{i18n.t("queues.table.greeting")}</TableCell>
                  <TableCell align="center">{i18n.t("queues.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queues.map((queue) => (
                  <TableRow key={queue.id}>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        #{queue.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.queueInfo}>
                        <Box
                          className={classes.queueColor}
                          style={{ backgroundColor: queue.color }}
                        >
                          {queue.name?.charAt(0).toUpperCase()}
                        </Box>
                        <Typography className={classes.queueName}>
                          {queue.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={queue.color}
                        size="small"
                        className={classes.colorChip}
                        style={{
                          backgroundColor: alpha(queue.color, 0.15),
                          color: queue.color,
                          border: `1px solid ${queue.color}`,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        <Box className={classes.orderBadge}>{queue.orderQueue}</Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={queue.greetingMessage || "Sem mensagem"}>
                        <Typography className={classes.greetingText}>
                          {queue.greetingMessage || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.editBtn}`}
                            onClick={() => handleEditQueue(queue)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.deleteBtn}`}
                            onClick={() => {
                              setSelectedQueue(queue);
                              setConfirmModalOpen(true);
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={6} />}
              </TableBody>
            </Table>

            {!loading && queues.length === 0 && (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>
                  <AccountTreeIcon style={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Nenhuma fila encontrada
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Crie sua primeira fila de atendimento
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Queues;
